import { Check, ChevronDown, Loader2 } from 'lucide-react';
import React, { useState } from 'react';
import { Button } from './ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';

interface InlineEditSelectProps {
  value: string;
  options: { value: string; label: string; variant?: string }[];
  onSave: (value: string) => Promise<void>;
  disabled?: boolean;
  className?: string;
  renderValue?: (value: string, label: string) => React.ReactNode;
}

const InlineEditSelect: React.FC<InlineEditSelectProps> = ({
  value,
  options,
  onSave,
  disabled = false,
  className = '',
  renderValue,
}) => {
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  const currentOption = options.find(opt => opt.value === value);
  const displayLabel = currentOption?.label || value;

  const handleSelect = async (newValue: string) => {
    if (newValue === value || disabled) return;

    setSaving(true);
    try {
      await onSave(newValue);
      setOpen(false);
    } catch (error) {
      console.error('Error saving:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={`h-auto py-1 px-2 justify-between hover:bg-muted ${className}`}
          disabled={disabled || saving}
        >
          {saving ? (
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Saving...</span>
            </div>
          ) : renderValue ? (
            renderValue(value, displayLabel)
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-sm">{displayLabel}</span>
              <ChevronDown className="h-3 w-3 opacity-50" />
            </div>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-48">
        {options.map((option) => (
          <DropdownMenuItem
            key={option.value}
            onClick={() => handleSelect(option.value)}
            className="cursor-pointer"
          >
            <div className="flex items-center justify-between w-full">
              <span>{option.label}</span>
              {option.value === value && <Check className="h-4 w-4" />}
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default InlineEditSelect;

